// Frontal-view feature extraction.
//
// All outputs are normalized, dimensionless magnitudes (scaled by torso length)
// so they are camera-distance invariant and longitudinally comparable. They are
// converted to categorical bands in bands.ts — no angles are reported.

import { Joints, isotropicFrame, torsoLength, type PoseFrame, type Side } from "../pose/landmarks";
import { singleSupportWindows, type EventResult } from "./events";
import { percentile, signedLineDistance, symmetryIndex } from "./geometry";
import { mean } from "./signal";

export interface SideMagnitude {
  L: number;
  R: number;
}

export interface FrontalFeatures {
  /** Peak pelvic obliquity (normalized) over the clip. */
  pelvicDrop: number;
  /** Contralateral pelvic drop during each side's single stance (Trendelenburg proxy). */
  pelvicDropStance: SideMagnitude;
  /** Trendelenburg pattern likelihood 0..1 (drop + compensatory trunk shift). */
  trendelenburg: number;
  /** Dynamic knee collapse (valgus) tendency per side, normalized. */
  kneeCollapse: SideMagnitude;
  /** Lateral trunk lean (normalized), peak over clip. */
  trunkLean: number;
  /** Step width (normalized mediolateral ankle separation at contacts). */
  stepWidth: number;
  /** Side-to-side asymmetry index (combines pelvic drop + knee collapse). */
  asymmetryIndex: number;
  /** Co-occurring compensation flags. */
  compensations: string[];
}

export function computeFrontalFeatures(
  frames: PoseFrame[],
  events: EventResult,
  aspect = 1,
): FrontalFeatures {
  // Isotropic image space so torso-normalized x-offsets aren't aspect-distorted.
  // aspect = 1 reproduces the previous (raw-coordinate) behaviour exactly.
  const fr = frames.map((f) => isotropicFrame(f, aspect));

  // --- Pelvic obliquity (signed): +ve = left hip lower than right ---
  const obliquity = fr.map((f) => {
    const j = new Joints(f);
    return (j.hip("L").y - j.hip("R").y) / safeTorso(f);
  });
  const pelvicDrop = percentile(obliquity.map(Math.abs), 90);

  // Drop during each side's single-limb stance (contralateral hip drops).
  const pelvicDropStance: SideMagnitude = {
    L: stanceMean(fr, events, "L", obliquity, true),
    R: stanceMean(fr, events, "R", obliquity, true),
  };

  // --- Knee collapse / valgus tendency per side ---
  // Signed perpendicular distance of knee from the hip→ankle line, normalized.
  // We orient so that medial deviation (toward midline) is positive.
  const kneeCollapse: SideMagnitude = {
    L: kneeCollapseSide(fr, events, "L"),
    R: kneeCollapseSide(fr, events, "R"),
  };

  // --- Trunk lean (lateral): shoulder-center horizontal offset from pelvis ---
  const trunkLeanSeries = fr.map((f) => {
    const j = new Joints(f);
    return Math.abs(j.shoulderCenter().x - j.pelvisCenter().x) / safeTorso(f);
  });
  const trunkLean = percentile(trunkLeanSeries, 90);

  // --- Step width at contacts (mediolateral ankle separation) ---
  const contacts = events.events.filter((e) => e.type === "HS");
  const widths = contacts.map((e) => {
    const f = fr[e.index];
    const j = new Joints(f);
    return Math.abs(j.ankle("L").x - j.ankle("R").x) / safeTorso(f);
  });
  const stepWidth = widths.length ? mean(widths) : 0;

  // --- Trendelenburg: drop magnitude + compensatory trunk shift to stance side ---
  // pelvicDropStance is y-based (aspect-invariant); trunkLean is an x-based ratio
  // computed in isotropic space, so its reference constant is the legacy 0.08
  // rescaled by the portrait frontal aspect (9/16 ≈ 0.045). See bands.ts.
  const trendelenburg = Math.min(
    1,
    (Math.max(pelvicDropStance.L, pelvicDropStance.R) / 0.06) * 0.6 +
      (trunkLean / 0.045) * 0.4,
  );

  // --- Side-to-side asymmetry ---
  const asymmetryIndex =
    0.5 * symmetryIndex(pelvicDropStance.L, pelvicDropStance.R) +
    0.5 * symmetryIndex(kneeCollapse.L, kneeCollapse.R);

  // --- Compensation flags (co-occurrence) ---
  // x-based cutoffs (trunkLean, stepWidth, kneeCollapse) rescaled to isotropic
  // space by the portrait frontal aspect (9/16); pelvicDrop is y-based. See bands.ts.
  const compensations: string[] = [];
  if (pelvicDrop > 0.05 && trunkLean > 0.034)
    compensations.push("Trunk shift compensating pelvic drop");
  if (stepWidth > 0.28) compensations.push("Wide-based gait");
  if (Math.max(kneeCollapse.L, kneeCollapse.R) > 0.034 && pelvicDrop > 0.05)
    compensations.push("Knee collapse with pelvic instability");

  return {
    pelvicDrop,
    pelvicDropStance,
    trendelenburg,
    kneeCollapse,
    trunkLean,
    stepWidth,
    asymmetryIndex,
    compensations,
  };
}

function kneeCollapseSide(
  frames: PoseFrame[],
  events: EventResult,
  side: Side,
): number {
  const windows = singleSupportWindows(events.events, side);
  const vals: number[] = [];
  const inWindow = (t: number) =>
    windows.length === 0 || windows.some((w) => t >= w.startMs && t <= w.endMs);

  for (const f of frames) {
    if (!inWindow(f.tMs)) continue;
    const j = new Joints(f);
    // Distance of knee from hip→ankle line; sign flipped per side so that
    // deviation toward the body midline (valgus tendency) reads positive.
    const d = signedLineDistance(j.knee(side), j.hip(side), j.ankle(side));
    const medial = side === "L" ? d : -d;
    vals.push(Math.max(0, medial) / safeTorso(f));
  }
  return vals.length ? percentile(vals, 75) : 0;
}

function stanceMean(
  frames: PoseFrame[],
  events: EventResult,
  side: Side,
  series: number[],
  contralateral: boolean,
): number {
  const windows = singleSupportWindows(events.events, side);
  if (windows.length === 0) return 0;
  const vals: number[] = [];
  for (let i = 0; i < frames.length; i++) {
    const t = frames[i].tMs;
    if (windows.some((w) => t >= w.startMs && t <= w.endMs)) {
      // Contralateral drop: during L stance, the R hip drops (obliquity sign).
      const v = series[i];
      vals.push(contralateral ? Math.abs(v) : v);
    }
  }
  return vals.length ? percentile(vals.map(Math.abs), 75) : 0;
}

function safeTorso(f: PoseFrame): number {
  const t = torsoLength(f);
  return t > 1e-4 ? t : 1e-4;
}
